import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Category } from 'common/type';
import { linkPath } from 'common/constants';
import { useResponsive } from 'common/context';
import { DashboardCategoroyElement } from 'features/dashboard/types';
import { ManageCategoriesAPI } from 'features/ManageCategories/ManageCategoriesService';
import { isGroupCategory } from 'features/Documents/groupRegistry';
import { isBirthdaysCategory } from 'common/utils/birthdaysCategory';
import { getOwnDocumentCount } from 'features/dashboard/lifecycle/lifecycleCards';
import { SHEPHERD_VERSION } from './constants';
import { getShepherdStepOrder } from './steps';
import { ShepherdRuntimeCtx, ShepherdTrigger } from './types';
import { markShepherdDone } from './shepherdSlice';
import { useSaveDashboardTourMutation } from './ShepherdService';
import ShepherdOverlay from './ShepherdOverlay';
import useShepherdTracking from './useShepherdTracking';

type ShepherdContextValue = {
  activeStepId: string | null;
  startTour: (trigger: ShepherdTrigger) => void;
  /** uncategorizedCount null/undefined = document count unknown (a load
   * failed): hasDocs keeps its previous value (default: full tour). */
  registerDashboardCategories: (cats: DashboardCategoroyElement[], uncategorizedCount?: number | null) => void;
  hiddenCategories: Category[];
};

const ShepherdContext = createContext<ShepherdContextValue | null>(null);

export const useShepherd = (): ShepherdContextValue => {
  const ctx = useContext(ShepherdContext);
  if (!ctx) throw new Error('useShepherd must be used within ShepherdProvider');
  return ctx;
};

export const ShepherdProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [saveDashboardTour] = useSaveDashboardTourMutation();
  const [fetchCategories] = ManageCategoriesAPI.endpoints.manageCategoriesList.useLazyQuery();
  const { shouldExpandForWeb } = useResponsive();
  const tracking = useShepherdTracking();

  // Id-keyed cursor (not a numeric index): eligibleSteps can reflow while the
  // tour is running (late-resolving categories fetch flips a condition), and an
  // insertion/removal behind a numeric index would re-point the step under the
  // user's cursor. Keying by id makes advancement relative to step identity.
  const [currentStepId, setCurrentStepId] = useState<string | null>(null); // null = not running
  const [hiddenCategories, setHiddenCategories] = useState<Category[]>([]);
  const firstDoqCategoryRef = useRef<DashboardCategoroyElement | null>(null);
  const firstDoqCategoryGroupCapableRef = useRef<boolean>(false);
  // Unknown (dashboard never registered) counts as having docs, which keeps
  // the full tour exactly as it was.
  const hasDocsRef = useRef<boolean>(true);
  // Snapshot of hasDocsRef taken at tour start so the step order cannot
  // change under a running tour.
  const [tourHasDocs, setTourHasDocs] = useState<boolean>(true);
  const expectedPathRef = useRef<string | null>(null);
  // Teardown re-entrancy latch: the route-mismatch and visibilitychange
  // effects can both call skip() in the same tick with the same stale step
  // closure, which without a guard would double-fire shepherd_skipped and
  // run finish()'s dispatch + backend PATCH twice. Set on first teardown,
  // reset by startTour.
  const hasEndedRef = useRef(false);

  const runtimeCtx: ShepherdRuntimeCtx = useMemo(() => ({
    firstDoqCategory: firstDoqCategoryRef.current,
    firstDoqCategoryGroupCapable: firstDoqCategoryGroupCapableRef.current,
    hiddenCategories,
    isDesktop: shouldExpandForWeb,
    hasDocs: tourHasDocs,
  }), [hiddenCategories, currentStepId, shouldExpandForWeb, tourHasDocs]);

  // Canonical order for this run: SHEPHERD_STEPS with docs, the trimmed
  // zero-document order without.
  const stepOrder = useMemo(() => getShepherdStepOrder(tourHasDocs), [tourHasDocs]);

  // Steps that can run this session (conditions evaluated at start AND on advance,
  // because hiddenCategories may arrive after the welcome step is already up).
  const eligibleSteps = useMemo(
    () => stepOrder.filter((s) => !s.condition || s.condition(runtimeCtx)),
    [stepOrder, runtimeCtx],
  );

  const running = currentStepId !== null;
  const step = running ? eligibleSteps.find((s) => s.id === currentStepId) ?? null : null;
  const stepPos = step ? eligibleSteps.indexOf(step) : -1;

  const finish = useCallback((completed: boolean) => {
    if (hasEndedRef.current) return;
    hasEndedRef.current = true;
    setCurrentStepId(null);
    expectedPathRef.current = null;
    dispatch(markShepherdDone({ version: SHEPHERD_VERSION }));
    saveDashboardTour({
      dashboard_tour: { completed_at: new Date().toISOString(), version: SHEPHERD_VERSION },
    }).unwrap().catch(() => { /* local flag still set; backend flag is best-effort */ });
    if (completed) {
      // Completion path (next() on the last step).
      tracking.trackCompleted();
      navigate(linkPath.dashboard);
    }
  }, [dispatch, navigate, saveDashboardTour, tracking.trackCompleted]);

  const startTour = useCallback((trigger: ShepherdTrigger) => {
    hasEndedRef.current = false;
    // Fires shepherd_started, plus shepherd_replayed when trigger === 'replay'
    // — see useShepherdTracking.
    tracking.trackStarted(trigger);
    fetchCategories().unwrap()
      .then((res) => setHiddenCategories((res.data || []).filter((c) => c.is_hidden)))
      .catch(() => setHiddenCategories([]));
    const hasDocs = hasDocsRef.current;
    setTourHasDocs(hasDocs);
    // First step of this run's order: welcome (unconditional) with docs,
    // add-doq (unconditional) without.
    setCurrentStepId(getShepherdStepOrder(hasDocs)[0].id);
  }, [fetchCategories, tracking.trackStarted]);

  const registerDashboardCategories = useCallback((
    cats: DashboardCategoroyElement[],
    uncategorizedCount?: number | null,
  ) => {
    if (uncategorizedCount != null) {
      hasDocsRef.current = getOwnDocumentCount(cats) + uncategorizedCount > 0;
    }
    // Birthdays can never anchor the tour's category phase: its tile opens
    // the birthdays screen, not the category page, so none of the
    // category-page tour targets exist there. Routing the tour at it killed
    // the run mid-way (869eeatb2 - a tester whose only docs were birthdays).
    const firstDoq = cats.find(
      (c) => c.documents_count > 0 && !isBirthdaysCategory(c.id, !!c.is_custom),
    ) || null;
    firstDoqCategoryRef.current = firstDoq;
    firstDoqCategoryGroupCapableRef.current = firstDoq
      ? isGroupCategory({ id: firstDoq.id, slug: firstDoq.slug, is_custom: firstDoq.is_custom })
      : false;
  }, []);

  const next = useCallback(() => {
    if (currentStepId === null) return;
    // Position computed at click time, so a reflow of eligibleSteps between
    // renders can never advance relative to a stale index.
    const pos = eligibleSteps.findIndex((s) => s.id === currentStepId);
    if (pos === -1) {
      // Defensive: the current step itself became ineligible. Advance to the
      // first eligible step after it in this run's canonical order.
      const canonicalPos = stepOrder.findIndex((s) => s.id === currentStepId);
      const fallback = eligibleSteps.find((s) => stepOrder.indexOf(s) > canonicalPos);
      if (fallback) {
        setCurrentStepId(fallback.id);
      } else {
        finish(true);
      }
      return;
    }
    if (pos >= eligibleSteps.length - 1) {
      finish(true);
    } else {
      setCurrentStepId(eligibleSteps[pos + 1].id);
    }
  }, [currentStepId, eligibleSteps, stepOrder, finish]);

  const skip = useCallback(() => {
    // Early-return BEFORE tracking: finish() also guards itself, but
    // trackSkipped fires ahead of finish(), so skip needs its own check to
    // keep shepherd_skipped single-fire under same-tick teardown races.
    if (hasEndedRef.current) return;
    // step can be null here (e.g. a teardown effect firing skip() during a
    // step reflow where the current id no longer resolves) — don't track a
    // skip with no step to attribute it to.
    if (step) tracking.trackSkipped(step.id, stepPos);
    finish(false);
  }, [step, stepPos, tracking.trackSkipped, finish]);

  // Route hop for the current step.
  useEffect(() => {
    if (!step) return;
    const dest = step.route(runtimeCtx);
    if (dest && dest.path !== location.pathname) {
      expectedPathRef.current = dest.path;
      navigate(dest.path, { state: dest.state });
    } else {
      expectedPathRef.current = location.pathname;
    }
  }, [step?.id]);

  // Analytics: one shepherd_step_viewed per step, including the first
  // (welcome). Deps mirror the route-hop effect above — keyed on step id so
  // an eligibleSteps reflow that doesn't change the current step never
  // re-fires this.
  useEffect(() => {
    if (!step) return;
    tracking.trackStepViewed(step.id, stepPos);
  }, [step?.id]);

  // User navigated somewhere the tour didn't ask for -> tear down as skip.
  useEffect(() => {
    if (!running || !expectedPathRef.current) return;
    // Category paths are dynamic; compare prefixes for the category hop.
    const expected = expectedPathRef.current;
    const ok = location.pathname === expected
      || (expected.startsWith(linkPath.dashboardCategory) && location.pathname.startsWith(linkPath.dashboardCategory));
    if (!ok) skip();
  }, [location.pathname]);

  // App backgrounded -> tear down as skip.
  useEffect(() => {
    if (!running) return undefined;
    const onVis = () => { if (document.visibilityState === 'hidden') skip(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [running, skip]);

  const value = useMemo(() => ({
    activeStepId: step?.id ?? null,
    startTour,
    registerDashboardCategories,
    hiddenCategories,
  }), [step?.id, startTour, registerDashboardCategories, hiddenCategories]);

  return (
    <ShepherdContext.Provider value={value}>
      {children}
      {running && step && (
        <ShepherdOverlay
          step={step}
          index={stepPos}
          total={eligibleSteps.length}
          onNext={next}
          onSkip={skip}
        />
      )}
    </ShepherdContext.Provider>
  );
};

export default ShepherdProvider;
